/**
 * Email verification UX flow helpers (Auth Security Batch 1b).
 *
 * Framework-free decision logic for the frontend verification experience:
 * post-auth routing, sensitive-route gating, token parsing, verify-result
 * state mapping, and detection of the backend 403 "Email verification
 * required" response. Kept pure so it can be unit-tested without rendering
 * React, matching the repo's lib-test convention.
 */

export const ROUTE_VERIFY_EMAIL = '/verify-email';
export const ROUTE_VERIFY_EMAIL_PENDING = '/verify-email-pending';

// Must match the detail string returned by the backend get_verified_user()
// dependency (backend/app/core/auth.py). Compared case-insensitively.
export const EMAIL_VERIFICATION_REQUIRED_DETAIL = 'Email verification required';

// Authenticated, sensitive surfaces that require a verified email before use.
// Public browse (/, /listings, /locations, /pricing, etc.) is intentionally
// excluded so logged-out and unverified visitors can still explore.
export const VERIFICATION_GATED_PREFIXES = ['/owner', '/dashboard', '/bookings'];

// Verify-email page UI states.
export const VERIFY_STATE_IDLE = 'idle';
export const VERIFY_STATE_MISSING = 'missing';
export const VERIFY_STATE_LOADING = 'loading';
export const VERIFY_STATE_SUCCESS = 'success';
export const VERIFY_STATE_INVALID = 'invalid';
export const VERIFY_STATE_ERROR = 'error';

export function isEmailVerified(user) {
  return Boolean(user) && user.email_verified === true;
}

/**
 * True only when we have a logged-in user whose email is explicitly
 * unverified. A missing email_verified field (older token/cache) is treated as
 * NOT gating, so we never trap a user the backend would actually allow.
 */
export function isUnverifiedUser(user) {
  return Boolean(user) && user.email_verified === false;
}

/**
 * Where to send a user immediately after register/login. Unverified users go
 * to the pending page; everyone else falls back to their normal destination.
 */
export function getPostAuthRedirect(user, fallbackRoute) {
  if (isUnverifiedUser(user)) {
    return ROUTE_VERIFY_EMAIL_PENDING;
  }
  return fallbackRoute;
}

export function isVerificationGatedPath(pathname) {
  const path = String(pathname || '');
  return VERIFICATION_GATED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Returns the pending route when an unverified user is on a gated path,
 * otherwise null. Logged-out users and verified users are never redirected.
 */
export function getVerificationGateRedirect(pathname, { user } = {}) {
  if (!isUnverifiedUser(user)) {
    return null;
  }
  if (!isVerificationGatedPath(pathname)) {
    return null;
  }
  return ROUTE_VERIFY_EMAIL_PENDING;
}

/**
 * Extract the verification token from a query string or URLSearchParams.
 * Accepts "?token=abc", "token=abc", or a URLSearchParams instance.
 */
export function parseVerificationToken(search) {
  if (!search) return '';
  let params;
  if (typeof URLSearchParams !== 'undefined' && search instanceof URLSearchParams) {
    params = search;
  } else {
    const raw = String(search);
    params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  }
  return (params.get('token') || '').trim();
}

/**
 * Map a failed verify-email request to a UI state.
 *  - 400 / 404 / 410 (bad, expired, used, missing record) -> invalid
 *  - anything else (no response, 5xx, network)            -> error
 */
export function mapVerifyErrorToState(error) {
  const status = error?.response?.status;
  if (status === 400 || status === 404 || status === 410) {
    return VERIFY_STATE_INVALID;
  }
  return VERIFY_STATE_ERROR;
}

/**
 * True when an axios error is the backend's 403 "Email verification required"
 * response, so callers can show a friendly prompt instead of a raw detail.
 */
export function isEmailVerificationRequiredError(error) {
  const status = error?.response?.status;
  if (status !== 403) return false;
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string'
    && detail.toLowerCase().includes('email verification required');
}
